// GDACS search API (GeoJSON, CORS open): orange and red alerts of the last two weeks.
// Earthquakes are left to USGS, which is faster and carries the magnitude.

import { addDays, fromDate } from '../../core/dates.ts';
import type { Fetcher } from '../types.ts';
import { getJson } from './http.ts';
import type { LiveKind, LiveRecord } from './types.ts';

const SEARCH = 'https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH';
const EVENT_TYPES = 'TC;FL;VO;DR;WF';
const ALERT_LEVELS = 'Orange;Red';
const WINDOW_DAYS = 14;
const PUBLISHER = 'GDACS';

const KIND: Record<string, LiveKind> = { TC: 'cyclone', FL: 'flood', VO: 'volcano', DR: 'drought', WF: 'wildfire' };

interface GdacsFeature {
  geometry: { type: string; coordinates: [number, number] } | null;
  properties: {
    eventtype: string;
    eventid: number;
    name: string;
    description: string;
    alertlevel: string;
    alertscore: number;
    iscurrent: string;
    datemodified: string;
    url: { report: string };
  };
}

export async function gdacsAlerts(fetcher: Fetcher, now: Date): Promise<LiveRecord[]> {
  const today = fromDate(now);
  const url = `${SEARCH}?eventlist=${EVENT_TYPES}&alertlevel=${ALERT_LEVELS}&fromDate=${addDays(today, -WINDOW_DAYS)}&toDate=${today}`;
  const doc = await getJson<{ features: GdacsFeature[] }>(fetcher, url);
  const out: LiveRecord[] = [];

  for (const f of doc.features) {
    const p = f.properties;
    const kind = KIND[p.eventtype];
    if (!kind || p.iscurrent !== 'true' || f.geometry?.type !== 'Point') {
      continue;
    }
    out.push({
      id: `gdacs:${p.eventtype}:${p.eventid}`,
      kind,
      title: `${p.alertlevel} alert: ${p.name || p.description}`,
      // GDACS sends UTC instants without a zone suffix.
      time: new Date(`${p.datemodified}Z`).toISOString(),
      lon: f.geometry.coordinates[0],
      lat: f.geometry.coordinates[1],
      url: p.url.report,
      publisher: PUBLISHER,
      severity: p.alertscore,
    });
  }
  return out;
}
